import {
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { config } from '../../../../config/config';
import { CreateEventCommand } from '../../../commands/hub/event/create.event.command';
import { DateTimeInputComponent } from '../../../common/components/datetime.input/datetime.input.component';
import { ModalService } from '../../../common/services/modal.service';
import { HubEvent } from '../../../models/hub/hub.event';
import { HubVisitorPromotionImage } from '../../../models/hub/hub.visitor.promotion.image';
import { EventTimeService } from '../../../services/event.time.service';
import { HubApiService } from '../../../services/hub.api.service';
import { ImageService } from '../../../services/image.service';
import { BaseComponent } from '../../../common/components/base.component';
import { MatDialogRef } from '@angular/material';
import { ButtonTriggerEvent } from '../../../common/components';
import { ResizeImageModalComponent } from '../resize.image.modal/resize.image.modal.component';
import { InfoModalData, ShowInfoModalActions } from '../show.info.modal/show.info.modal.provider';
import { EditEventModalActions } from './edit.event.modal.provider';
import { clone } from '../../../../../server/src/utils/object.extensions';
import {
  ResizeImageModalActions,
  ResizeImageModalData,
} from '../resize.image.modal/resize.image.modal.provider';
import { ShowInfoModalComponent } from '../show.info.modal/show.info.modal.component';
import { localStorageKeys } from '../../../common/constants/local.storage.keys';
import { VisitorSettingsCommand } from '../../../commands/pagination.query.command';
import { ToggleStore } from '../../../stores/hub.calendar.store';
import { HubVisitorFeatureToggle } from '../../../models/hub/hub.visitor.feature.toggle';
import { isNullOrUndefined } from '../../../common/extensions';
import { ShowImageModalComponent } from '../show.image.modal/show.image.modal.component';
import {
  ShowImageModalActions,
  ShowImageModalData,
} from '../show.image.modal/show.image.modal.provider';
import * as jwt_decode from 'jwt-decode';
import { TokenInfo } from '../../../models/auth/token.info';
import { AlertifyService } from '../../../services/alertify.service';

@Component({
  templateUrl: './edit.event.modal.component.html',
  styleUrls: ['./edit.event.modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EditEventModalComponent extends BaseComponent {
  public event: HubEvent;
  public hubName: string;
  public category = 'OTHER';
  public timezoneAddress: any;
  public showTimezoneCitySelector = false;
  public isLoadingTimezone = false;
  public isUploadImage = false;
  public imageLink: string;
  public isShowMore = false;
  public isEditDescription = false;
  public isDescriptionNeedShowMore = false;
  public createEventForm: FormGroup;
  public selectedImageData: SafeUrl | null;
  public selectedImageFile: File;
  public sizeLimit = config.eventImageSizeLimit;
  public createEventCommand: CreateEventCommand;
  public frequencyItems = [];
  public frequencyEventTimes = [];
  public totalTimes = '';
  public dayInWeek = -1;
  public weekNo = -1;
  public frequency = 'event';
  public defaultEventTimes = [];
  public isAllowOnlineEvents = true;
  public isImageChanged = false;
  public hasCopyrights = false;
  public resizeImageTemp = null;
  public toggles = new HubVisitorFeatureToggle();
  public isRefreshingImage = false;
  public isGeneratingDescription = false;
  public aiGeneratedDescription: string[] = [];
  public firstTicketUri = '';
  public secondTicketUri = '';
  public descriptionEditor;
  private originalImageLink: string;

  public visitorPromotionImage = new HubVisitorPromotionImage();
  @ViewChild('description') public descriptionArea: ElementRef;
  @ViewChild('descriptionHtml') public descriptionHtml: ElementRef;
  @ViewChild('imageSelector') public imageSelector: ElementRef;
  @ViewChild('img') public imgEle: ElementRef;
  @ViewChild('mask') public mask: ElementRef;
  @ViewChild('upload') public upload: ElementRef;
  @ViewChild('icon') public icon: ElementRef;
  @ViewChildren('dateTimeInput') public dateTimeInput: QueryList<DateTimeInputComponent>;

  constructor(
    private dialogRef: MatDialogRef<EditEventModalComponent>,
    private actions: EditEventModalActions,
    private hubApiService: HubApiService,
    public imageService: ImageService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private togglesStore: ToggleStore,
    private alertify: AlertifyService,
    // private scriptService: ScriptService,
    data: HubEvent
  ) {
    super();
    this.event = Object.assign({}, data);

    if (isNullOrUndefined(this.event.owner)) {
      this.event.owner = {
        name: '',
        id: '',
      };
    }

    this.hubName = this.event.hubName;

    if (!this.event.eventTimes || !this.event.eventTimes.length) {
      this.event.eventTimes = [];
      this.event.eventTimes.push({
        startTime: this.event.startTime,
        endTime: this.event.endTime,
      });
    }

    this.event.timezoneFormatter =
      this.event.timezoneFormatter ||
      (this.event.timezone
        ? EventTimeService.getTimezoneFormatterByTimezone(this.event.timezone)
        : EventTimeService.getTimezoneFormatter());
    if (this.event.timezone && this.event.timezone.startsWith('+')) {
      this.event.timezone = null;
    }
    this.event.timezone =
      this.event.timezone ||
      EventTimeService.getTimezoneCodeByFormatter(this.event.timezoneFormatter);
    this.event.isNoTime = EventTimeService.isNoTimeEvent(this.event);

    this.defaultEventTimes = clone(this.event.eventTimes);

    this.createForm();
    this.createEventCommand = new CreateEventCommand();
    this.imageLink = this.imageService.imageSource(this.event);
    this.imageWayChange();
    this.event.imageFile = null;

    this.resetFrequencyDropdown();

    // for no description event, enter the edit mode directly
    //this.isEditDescription = !this.event.description;
    this.descriptionChange();

    this.loadIsAllowOnlineEvents();
    this.getToggles();

    this.setUpHtmlEditor();

    this.imageLink = this.imageService.imageSource(this.event) as string;
    this.originalImageLink = this.imageLink;
  }

  public setEventTime(time, timeRange) {
    if (time.startTime) {
      timeRange.startTime = time.startTime;
    }
    if (time.endTime) {
      timeRange.endTime = time.endTime;
    }
    if (this.frequency !== 'custom' && this.frequency !== 'event') {
      this.frequencySourceChange(this.frequency);
    }
  }

  public showMore() {
    this.isShowMore = !this.isShowMore;
  }

  public setUpHtmlEditor() {
    // set up the description HTML editor
    // http://suneditor.com/sample/html/examples.html
    // https://www.cssscript.com/tag/wysiwyg-editor/
    setTimeout(() => {
      this.descriptionEditor = window['SUNEDITOR'].create('description-editor', {
        toolbarContainer: '#toolbar_container',
        showPathLabel: false,
        charCounter: true,
        // maxCharCount: 720,
        width: 'auto',
        // maxWidth: '700px',
        height: 'auto',
        minHeight: '100px',
        maxHeight: '250px',
        value: this.event.description || '',
        buttonList: [
          ['undo', 'redo', 'font', 'fontSize', 'formatBlock'],
          ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'removeFormat'],
          [
            'fontColor',
            'hiliteColor',
            'outdent',
            'indent',
            'align',
            'horizontalRule',
            'list',
            'table',
          ],
          ['link', 'image', 'video', 'fullScreen', 'showBlocks', 'codeView', 'preview', 'print'],
        ],
        // callBackSave: function (contents, isChanged) {
        //   console.log(contents);
        // },
      });

      this.descriptionEditor.onImageUploadBefore = (files, info, core, uploadHandler) => {
        try {
          this.handleImageUploadBefore(files, info, uploadHandler);
        } catch (err) {
          uploadHandler(err.toString());
        }
      };
    });
  }

  // public showHTML() {
  //   const myContent = window['tinymce'].activeEditor.getContent();
  //   console.log('myContent', myContent);
  //   this.removeHtmlEditor();
  //   this.event.description = myContent;
  //   this.isEditDescription = false;
  // }

  public editDescription($event) {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }
    this.isEditDescription = !this.isEditDescription;

    // this.setHtmlEditor();

    this.descriptionChange();
  }

  public close(): void {
    if (this.actions.cancel) {
      this.actions.cancel();
    }

    this.dialogRef.close();
  }

  public async confirm(event: ButtonTriggerEvent) {
    this.setEventTimes();
    this.event.imageLink = this.imageLink;
    this.event.title = this.event.name;
    this.setEventLinks();

    this.event.description = this.descriptionEditor.getContents();
    //Need research why wangEditor get default value is "<p><br></p>" when empty
    // if(this.event.description === "<p><br></p>"){
    //   this.event.description = "";
    // }

    const linkHasChanged = (this.imageLink || '').trim() !== (this.originalImageLink || '').trim();

    // Initialize cover object if needed
    if (linkHasChanged || !this.event.cover) {
      this.event.cover = {
        source: this.imageLink,
        isLarge: true,
      };
    }

    // Handle AWS upload and wait for completion
    if (
      this.resizeImageTemp ||
      (linkHasChanged &&
        this.imageLink &&
        (!this.imageLink.startsWith('https://iti-images.s3.amazonaws.com') ||
          this.imageLink.indexOf('.webp') < 0))
    ) {
      try {
        // Wait for AWS upload to complete
        const res = await this.hubApiService.uploadBase64ImageToAWS(
          this.resizeImageTemp || this.imageLink,
          1200
        );

        if (res.success) {
          // Ensure cover object exists and update with AWS URL
          if (this.event.cover) {
            this.event.cover = { isLarge: true };
          }
          this.event.cover.source = this.imageLink = this.event.imageLink = res.url;
        } else {
          // Handle upload failure
          console.error('AWS upload failed:', res);
          this.alertify.warning(
            '🚨 Failed to upload image. Please try to download the image and upload from your computer.'
          );
          return; // Don't proceed with event update if upload failed
        }
      } catch (error) {
        console.error('Error uploading to AWS:', error);
        return; // Don't proceed with event update if upload failed
      }
    }

    // Handle address components
    if (
      this.event.address &&
      this.event.address.address_components &&
      this.event.address.address_components.length > 0
    ) {
      this.event.address.addressComponents = clone(this.event.address.address_components);
      this.event.address.address_components = null;
    }

    this.hubApiService
      .updateEvent(this.hubName, this.event._id, this.event)
      .subscribe((newEvent) => {
        this.event = newEvent;
        EventTimeService.processEventTime(this.event);
        this.actions.confirm(
          event,
          this.event,
          !this.isUploadImage ? this.imageLink : newEvent.cover.source
        );

        if (!window.location.href.includes('localhost')) {
          const authToken = localStorage.getItem(localStorageKeys.authToken);
          let tokenInfo = jwt_decode<TokenInfo>(authToken);
          try {
            let trackName = 'editEvent - ' + this.event.name;
            window['pendo'].track(trackName, {
              accountId: tokenInfo.userName,
              timestamp: Date.now(),
              hubName: this.hubName,
            });
          } catch (error) {
            console.error(error);
          }
        }

        this.dialogRef.close();
      });
  }

  public showChangeHistory() {
    let text = [];
    this.event.changeHistory.forEach((x, i) => {
      text.push(`${++i}. Changed by: ${x.userName}, at ${new Date(x.changedAt).toLocaleString()} `);
    });

    this.modalService.show({
      component: ShowInfoModalComponent,
      providers: [
        { provide: ShowInfoModalActions, useValue: null },
        {
          provide: InfoModalData,
          useValue: new InfoModalData('Result', [], text.length ? text : ['(No records)']),
        },
      ],
      panelClass: 'width-modal-3',
    });
  }

  public citySelected() {
    if (this.timezoneAddress) {
      this.isLoadingTimezone = true;
      this.hubApiService
        .getTimezoneByLatlng(this.timezoneAddress.lat, this.timezoneAddress.lng)
        .finally(() => {
          this.isLoadingTimezone = false;
        })
        .subscribe((data) => {
          console.log(data.abbreviation);
          this.event.timezone = data.abbreviation;
          this.event.timezoneFormatter = EventTimeService.getTimezoneFormatterByTimezone(
            this.event.timezone
          );

          this.dateTimeInput.toArray().forEach((x) => {
            x.timeFormatter = this.event.timezoneFormatter;
            x.timezone = this.event.timezone;
            x.setTime('start');
            x.setTime('end');
          });
        });
    }
  }

  public addTimeRange() {
    this.event.eventTimes.push({});
  }

  public removeTimeRange(i) {
    this.event.eventTimes.splice(i, 1);
  }

  public descriptionChange() {
    setTimeout(() => {
      if (this.descriptionArea && this.descriptionArea.nativeElement) {
        this.descriptionArea.nativeElement.style.height =
          Math.min(this.descriptionArea.nativeElement.scrollHeight, 300) + 'px';
      }
      if (this.descriptionHtml) {
        this.isDescriptionNeedShowMore = this.descriptionHtml.nativeElement.scrollHeight > 150;
      }
    });
  }

  public loadIsAllowOnlineEvents() {
    const settingsStr = localStorage.getItem(localStorageKeys.allBackendSettings);
    if (settingsStr) {
      const settings = JSON.parse(settingsStr);
      const customSettings = settings.customSettings as VisitorSettingsCommand;
      this.isAllowOnlineEvents = customSettings.isAllowOnlineEvents;
    }
  }

  public getToggles() {
    this.togglesStore.toggleCommand
      .takeUntil(this.isDestroyed)
      .subscribe((toggleCommand: HubVisitorFeatureToggle) => {
        this.toggles =
          isNullOrUndefined(toggleCommand) || isNullOrUndefined(toggleCommand.placeCalendar)
            ? new HubVisitorFeatureToggle()
            : toggleCommand;
      });
  }

  public imageWayChange() {
    setTimeout(() => {
      if (this.isUploadImage) {
        if (this.mask) {
          this.mask.nativeElement.style.height = '40px';
          this.mask.nativeElement.style.cursor = 'pointer';
          this.mask.nativeElement.style.backgroundColor = 'black';
          // this.mask.nativeElement.style.top = this.imgEle.nativeElement.height - 10 + 'px';
          this.mask.nativeElement.style.top = '35px';
        }
        if (this.upload) {
          this.upload.nativeElement.style.cursor = 'pointer';
          this.upload.nativeElement.style.backgroundColor = '#D3D0D0';
        }
      } else {
        this.selectedImageData = null;
        this.resizeImageTemp = null;
        if (this.mask) {
          this.mask.nativeElement.style.height = this.imgEle.nativeElement.height + 'px';
          this.mask.nativeElement.style.cursor = 'default';
          this.mask.nativeElement.style.backgroundColor = 'gray';
          this.mask.nativeElement.style.top = '30px';
        }
        if (this.upload) {
          this.upload.nativeElement.style.cursor = 'default';
          this.upload.nativeElement.style.backgroundColor = '#ececec';
        }
      }
    });
  }

  public async onImageChange($event) {
    this.selectedImageFile = $event.srcElement.files[0];

    if (!!this.selectedImageFile) {
      //if (this.selectedImageFile.size <= this.sizeLimit) {
      let reader = new FileReader();

      reader.onload = (e) => {
        this.selectedImageData = e.target['result'];
        // this.event.imageFile = this.selectedImageFile;
        this.imageWayChange();
      };

      reader.readAsDataURL(this.selectedImageFile);
      // this.event.imageFile = await this.imageService.compressImageFile(this.selectedImageFile, config.compressImgWidth);
      this.resizeImageTemp = await this.imageService.compressImageFileToBase64(
        this.selectedImageFile,
        config.compressImgWidth
      );
      // max limit size is 1MB
      if (this.resizeImageTemp.length > 1000000) {
        this.resizeImageTemp = await this.imageService.compressImageFileToBase64(
          this.selectedImageFile,
          config.compressImgWidth - 200
        );
      }

      this.isImageChanged = true;
    }
    $event.target.value = '';
  }

  public get isFormInvalid() {
    return (
      !this.event.name ||
      (!this.event.address && !this.event.isOnlineEvent) ||
      !this.event.owner.name ||
      !this.event.eventTimes ||
      !this.event.eventTimes.length ||
      (this.isImageChanged && !this.hasCopyrights)
    );
  }

  public virtualEventChange() {
    if (this.event.isOnlineEvent) {
      this.event.isHybridEvent = false;
    }
  }

  public hybridEventChange() {
    if (this.event.isHybridEvent) {
      this.event.isOnlineEvent = false;
    }
  }

  public get addressInputLabel() {
    if (!this.isAllowOnlineEvents) {
      return `Location / Address *`;
    }
    return `Location / Address ${this.event.isOnlineEvent ? '' : '*'} (or check Virtual Events)`;
  }

  public setFrequencyDropdown() {
    if (this.event.eventTimes.length > 0) {
      this.setDefaultTimeValue();
    }

    this.frequencyItems = this.frequencyItems.filter((x) => x.value !== 'last');

    let start = this.event.eventTimes[0].startTime;
    this.dayInWeek = EventTimeService.getDayOfWeek(start, this.event.timezone);
    let dayText = config.dayOfWeek[this.dayInWeek].name;

    let weekly = this.frequencyItems.first((x) => x.value === 'weekly');
    if (weekly) {
      weekly.text = `Weekly on ${dayText}`;
    }

    let biWeekly = this.frequencyItems.first((x) => x.value === 'biweekly');
    if (biWeekly) {
      biWeekly.text = `Every Other ${dayText} (starts from ${EventTimeService.getDayAndMonth(
        start,
        this.event.timezone
      )})`;
    }

    let monthly = this.frequencyItems.first((x) => x.value === 'monthly');
    if (monthly) {
      let no = EventTimeService.getWeekDayNo(start, this.event.timezone);
      if (!no.isLast) {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
      } else if (no.isLast && no.text === 'last') {
        monthly.text = `Monthly on the last ${dayText}`;
        monthly.value = 'last';
      } else if (no.isLast && no.text !== 'last') {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';

        this.frequencyItems.push({
          value: 'last',
          text: `Monthly on the last ${dayText}`,
        });
      }
    }
  }

  public frequencySourceChange(target) {
    this.frequency = target.value || target;
    let start = this.event.eventTimes[0].startTime;
    let end = this.event.eventTimes[0].endTime;

    // end = EventTimeService.addHours(new Date(end), -1);

    let eventTimes = [],
      days = [];
    let diff = 0;
    let scrossDaylight = '';

    if (this.frequency === 'event') {
      this.event.eventTimes = clone(this.defaultEventTimes);
      return;
    } else {
      this.event.eventTimes = [this.event.eventTimes[0]];
    }

    switch (this.frequency) {
      case 'daily':
        diff = EventTimeService.diff(start, end);
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonth(
          start,
          this.event.timezone
        )} - ${EventTimeService.getDayAndMonth(end, this.event.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * i);

          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'weekly':
        diff = EventTimeService.diff(start, end, 'weeks');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.event.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.event.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 7 * i);

          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'biweekly':
        diff = EventTimeService.diff(start, end, 'biweekly');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.event.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.event.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 14 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'monthly':
        days = EventTimeService.getSameDayInWeek(
          start,
          end,
          this.dayInWeek,
          this.weekNo,
          this.event.timezone
        );
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.event.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.event.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        });
        break;
      case 'last':
        days = EventTimeService.getLastWeekDays(start, end, this.dayInWeek, this.event.timezone);
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.event.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.event.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        });

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      default:
        break;
    }
    this.frequencyEventTimes = eventTimes;
  }

  public imageResized(imgBase64: string) {
    this.resizeImageTemp = imgBase64;
  }

  public resizeImage($event) {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }

    let actions = <ResizeImageModalActions>{
      confirm: (event: ButtonTriggerEvent, image: string) => {
        !!image && (this.resizeImageTemp = image);
      },
    };

    let data = <ResizeImageModalData>{
      data: this.resizeImageTemp || this.imageLink || this.event.cover.source,
    };

    this.modalService.show({
      component: ResizeImageModalComponent,
      providers: [
        { provide: ResizeImageModalActions, useValue: actions },
        { provide: ResizeImageModalData, useValue: data },
      ],
      panelClass: 'width-modal-2',
    });
  }

  public refreshImage() {
    this.isRefreshingImage = true;
    this.hubApiService
      .refreshEventImage(this.hubName, this.event._id)
      .finally(() => (this.isRefreshingImage = false))
      .subscribe((data) => {
        if (!data.success) {
          alert(`Unable to refresh this image, reason: ${data.error}`);
          return;
        }
        if (data && data.image) {
          let actions = <ShowImageModalActions>{
            confirm: (event: ButtonTriggerEvent, image: string) => {
              if (image) {
                this.imageLink = image;
              }
            },
          };
          this.modalService.show({
            component: ShowImageModalComponent,
            providers: [
              { provide: ShowImageModalActions, useValue: actions },
              {
                provide: ShowImageModalData,
                useValue: <ShowImageModalData>{
                  data: data.image,
                },
              },
            ],
            panelClass: 'width-modal-2',
          });
        }
      });
  }

  public generateDescription() {
    if (this.isGeneratingDescription) return;
    this.isGeneratingDescription = true;
    // this.aiGeneratedDescription = [];
    this.hubApiService
      .aiAssistant(this.hubName, {
        query: this.getEventInfoForGeneratingDescription(),
        model: 'chatgpt',
        feature: 'event-description',
      })
      .finally(() => {
        this.isGeneratingDescription = false;
      })
      .subscribe((data: { success: boolean; model: string; data: string }) => {
        if (data.success) {
          try {
            const json = JSON.parse(data.data);
            this.aiGeneratedDescription = [json.version1, json.version2, json.version3];
            console.log(this.aiGeneratedDescription);

            const modal = document.querySelector('.ig-edit-event-modal');
            modal &&
              (modal.className = modal.className.replace('width-modal-2', 'width-modal-70p'));

            this.trackEventAIContentAssistant();
          } catch (err) {
            console.error(err);
          }
        }
      });
  }

  public selectDescription(description) {
    if (this.isRegeneratingDescription) return;

    this.event.description = description;
    if (this.descriptionHtml) {
      this.descriptionHtml.nativeElement.classList.remove('ig-fade-in');
      this.descriptionHtml.nativeElement.style.height = '130px';
      setTimeout(() => {
        this.descriptionHtml.nativeElement.classList.add('ig-fade-in');
      });
    }
    if (this.descriptionArea) {
      this.descriptionArea.nativeElement.classList.remove('ig-fade-in');
      this.descriptionArea.nativeElement.style.height = '130px';
      setTimeout(() => {
        this.descriptionArea.nativeElement.classList.add('ig-fade-in');
      });
    }

    if (this.descriptionEditor) {
      this.descriptionEditor.setContents(description);
    }
  }

  public get isRegeneratingDescription() {
    return this.aiGeneratedDescription.length && this.isGeneratingDescription;
  }

  public get isNormalTicketUri(): boolean {
    if (!this.event) return false;
    return !!(
      this.event.ticketUri &&
      (!this.event.ticketInfo ||
        !this.event.ticketInfo.length ||
        this.event.ticketUri !== this.event.ticketInfo[0].link)
    );
  }

  private trackEventAIContentAssistant() {
    if (!window.location.href.includes('localhost')) {
      const authToken = localStorage.getItem(localStorageKeys.authToken);
      let tokenInfo = jwt_decode<TokenInfo>(authToken);
      try {
        let trackName = 'click - Event - AIContentAssistant - ' + this.event.name;
        window['pendo'].track(trackName, {
          accountId: tokenInfo.userName,
          timestamp: Date.now(),
          hubName: this.hubName,
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  private getEventInfoForGeneratingDescription() {
    return `event title: ${this.event.name}; address: ${
      this.event.address ? this.event.address.address : ''
    }; venue/host: ${
      this.event.venue || (this.event.venueInfo ? this.event.venueInfo.name : this.event.owner.name)
    }`;
  }

  private adjustForDaylightSaving(start, end, nextDayStart) {
    const formatter = EventTimeService.getTimezoneFormatterByTimezone(this.event.timezone);
    const startTimezone = EventTimeService.getTimezonecodeWithTimeSaving(start, formatter);
    const nextDayTimezone = EventTimeService.getTimezonecodeWithTimeSaving(nextDayStart, formatter);
    let scrossDaylight = '';
    let thisEnd = end;
    if (startTimezone !== nextDayTimezone) {
      if (nextDayTimezone.indexOf('ST') >= 0) {
        nextDayStart = EventTimeService.addHours(nextDayStart, 1);
        thisEnd = EventTimeService.addHours(thisEnd, 1);
        scrossDaylight = 'ST';
      } else {
        nextDayStart = EventTimeService.addHours(nextDayStart, -1);
        thisEnd = EventTimeService.addHours(thisEnd, -1);
        scrossDaylight = 'DT';
      }
    }
    return {
      startTime: nextDayStart,
      endTime: thisEnd,
      scrossDaylight,
    };
  }

  private setTimes(start, end, eventTimes) {
    let startTime = start;
    let endTime = new Date(new Date(startTime).setUTCHours(new Date(end).getUTCHours()));
    endTime = new Date(endTime.setUTCMinutes(new Date(end).getUTCMinutes()));
    if (endTime < new Date(startTime)) {
      endTime = new Date(EventTimeService.addHours(endTime, 24));
    }
    eventTimes.push({
      startTime: startTime,
      endTime: endTime.toISOString(),
    });
    console.log('eventTimes', eventTimes);
  }

  private setDefaultTimeValue() {
    let endPlusHours = 0;
    switch (this.frequency) {
      case 'custom':
        endPlusHours = 24 + 3;
        break;
      case 'daily':
        endPlusHours = 24 * 7 + 3;
        break;
      case 'weekly':
        endPlusHours = 24 * 365 + 3;
        break;
    }
    if (!this.event.eventTimes[0].startTime && !this.event.eventTimes[0].endTime) {
      this.event.eventTimes = [
        {
          startTime: EventTimeService.getDefaultStartTime(this.event.timezone),
          endTime: EventTimeService.addHours(
            EventTimeService.getDefaultStartTime(this.event.timezone),
            endPlusHours
          ),
        },
      ];
    } else if (this.event.eventTimes[0].startTime && !this.event.eventTimes[0].endTime) {
      this.event.eventTimes = [
        {
          startTime: this.event.eventTimes[0].startTime,
          endTime: EventTimeService.addHours(new Date(this.event.eventTimes[0].startTime), 24 * 7),
        },
      ];
    } else if (!this.event.eventTimes[0].startTime && this.event.eventTimes[0].endTime) {
      this.event.eventTimes = [
        {
          startTime: EventTimeService.addHours(
            new Date(this.event.eventTimes[0].startTime),
            24 * 7 * -1
          ),
          endTime: this.event.eventTimes[0].endTime,
        },
      ];
    }
  }

  private resetFrequencyDropdown() {
    this.frequencyItems = [
      {
        text: 'Current event times',
        value: 'event',
        selected: true,
      },
      {
        text: 'Custom',
        value: 'custom',
      },
      {
        text: 'Daily',
        value: 'daily',
      },
      {
        text: 'Weekly',
        value: 'weekly',
      },
      {
        text: 'Biweekly',
        value: 'biweekly',
      },
      {
        text: 'Monthly',
        value: 'monthly',
      },
    ];
  }

  private setEventTimes() {
    this.event.startTime = this.event.eventTimes[0].startTime;
    this.event.endTime = this.event.eventTimes[this.event.eventTimes.length - 1].endTime
      ? this.event.eventTimes[this.event.eventTimes.length - 1].endTime
      : null;
    if (this.frequency !== 'custom' && this.frequency !== 'event') {
      this.event.eventTimes = this.frequencyEventTimes;
    }
    this.event.eventTimes.forEach((x) => {
      x.startTimeFormatted = undefined;
      x.endTimeFormatted = undefined;
      x.startTimeText = undefined;
      x.endTimeText = undefined;
    });
    this.event.eventTimesText = [];
    this.event.startTimeFormatted = undefined;
    this.event.endTimeFormatted = undefined;
    this.event.startTimeText = undefined;
    this.event.endTimeText = undefined;
  }

  private setEventLinks() {
    this.event.eventLink = this.event.eventLink
      ? this.event.eventLink.startsWith('http')
        ? this.event.eventLink
        : `https://${this.event.eventLink}`
      : '';
    this.event.hostLink = this.event.hostLink
      ? this.event.hostLink.startsWith('http')
        ? this.event.hostLink
        : `https://${this.event.hostLink}`
      : '';
    this.event.ticketUri = this.event.ticketUri
      ? this.event.ticketUri.startsWith('http')
        ? this.event.ticketUri
        : `https://${this.event.ticketUri}`
      : '';

    // only take the 2 ticket urls (there might be more than 2 for existing serp google events, but also have restricted to get only 2 now)
    if (this.event.ticketInfo && this.event.ticketInfo.length) {
      let ticketInfo: any[] = [];
      for (const x of this.event.ticketInfo.slice(0, 2)) {
        x.link && ticketInfo.push(x);
      }
      this.event.ticketInfo = ticketInfo;
      this.event.ticketUri = '';
    }
  }

  private createForm() {
    this.createEventForm = this.formBuilder.group({
      name: [null, [Validators.required]],
      description: [null, []],
      timeRanges: this.formBuilder.array([]),
      address: [null, [Validators.required]],
      host: [null, [Validators.required]],
      category: [null, [Validators.required]],
      ticketUri: [null, []],
      ticketUri2: [null, []],
      facebookEvent: [null, []],
      hostLink: [null, []],
      eventLink: [null, []],
      imageLink: [null, []],
      timezone: [null, []],
      venue: [null, []],
      organizerEmail: [null, []],
    });
  }

  private handleImageUploadBefore(files, info, uploadHandler) {
    this.imageService
      .getBase64ByImageFile(files[0])
      .then((imageInfo) => {
        if (imageInfo) {
          this.hubApiService
            .uploadBase64ImageToAWS(imageInfo)
            .then((res) => {
              if (res.success) {
                const response = {
                  result: [
                    {
                      url: `${res.url}`,
                      name: files[0].name,
                      size: files[0].size,
                    },
                  ],
                };
                uploadHandler(response);
              } else {
                uploadHandler('upload image fail');
              }
            })
            .catch((err) => {
              uploadHandler('upload image fail');
              console.error(err);
            });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  public handleImageLinkChange(value: string): void {
    this.imageLink = value;

    // compare the *trimmed* current value with the original one
    const current = (this.imageLink || '').trim();
    const original = (this.originalImageLink || '').trim();

    this.isImageChanged = current !== original;
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/edit.event.modal/edit.event.modal.component.ts